// Both routes render the analytics dashboard and both accept the deep link's
// ?story=/?version= params: the second is the chrome-less embed HubSpot opens in
// its timeline modal.
const ANALYTICS_PATHS = [
  "/pages/analytics",
  "/pages/integrations/hubspot-analytics",
];
const OBJECT_ID = /^[a-f\d]{24}$/i;

export type TUrlSelection = { storyId: string; versionId: string };

const isAnalyticsPath = () =>
  typeof window !== "undefined" &&
  ANALYTICS_PATHS.some((path) => window.location.pathname.startsWith(path));

const readParam = (params: URLSearchParams, key: string) => {
  const value = params.get(key);
  // HubSpot builds these links from ClickHouse session rows, so a missing field
  // can arrive as the literal string "undefined". Only ids that could match a
  // document are worth turning into a filter — anything else would silently
  // empty every widget on the page.
  return value && OBJECT_ID.test(value) ? value : "";
};

export const readUrlSelection = (): TUrlSelection => {
  if (!isAnalyticsPath()) return { storyId: "", versionId: "" };
  const params = new URLSearchParams(window.location.search);
  return {
    storyId: readParam(params, "story"),
    versionId: readParam(params, "version"),
  };
};

/**
 * Keeps the query string in step with the story/version filters.
 *
 * The URL is the source of truth for this selection. The previous version read
 * the params once and immediately deleted them, which made every HubSpot deep
 * link single-use: a refresh, a back navigation, or the store reset that runs
 * when the analytics page unmounts all dropped the selection with no way to
 * recover it — so the page came back unfiltered.
 *
 * Only the keys present in `selection` are touched, so callers can update the
 * story without disturbing the version. `history.replaceState` is deliberate:
 * changing a filter should not push a new history entry.
 */
export const writeUrlSelection = (selection: Partial<TUrlSelection>) => {
  if (!isAnalyticsPath()) return;
  const params = new URLSearchParams(window.location.search);

  if ("storyId" in selection) {
    if (selection.storyId) params.set("story", selection.storyId);
    else params.delete("story");
  }
  if ("versionId" in selection) {
    if (selection.versionId) params.set("version", selection.versionId);
    else params.delete("version");
  }

  const search = params.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}`,
  );
};
